import React, { useEffect, useState, useRef } from "react";
import Lottie from "lottie-react";
import lightAnimation from "../animations/loading-light.json";
import darkAnimation from "../animations/loading-dark.json";
import { useTheme } from "../lib/ThemeContext";
import { Logo } from "./Logo";
import { useCenterSwitching, finishCenterSwitch } from "../lib/centerTransition";

const DISPLAY_DURATION = 3000; // ms to show animation (data loads behind it)
const FADE_DURATION = 400;

export function CenterTransitionOverlay() {
  const switchId = useCenterSwitching();
  const { isDark } = useTheme();

  const [visible, setVisible] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (switchId > 0) {
      // Show the overlay
      setVisible(true);
      setFadeOut(false);
      setFadeIn(false);

      // Fade in after a tick
      const fadeInTimer = setTimeout(() => setFadeIn(true), 50);

      // After display duration, start fade out
      timerRef.current = setTimeout(() => {
        setFadeOut(true);
      }, DISPLAY_DURATION);

      // After fade out completes, remove overlay and finish transition
      fadeTimerRef.current = setTimeout(() => {
        setVisible(false);
        setFadeIn(false);
        setFadeOut(false);
        finishCenterSwitch();
      }, DISPLAY_DURATION + FADE_DURATION);

      return () => {
        clearTimeout(fadeInTimer);
        clearTimeout(timerRef.current);
        clearTimeout(fadeTimerRef.current);
      };
    }
  }, [switchId]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-bg flex flex-col items-center justify-center transition-opacity"
      style={{
        opacity: fadeOut ? 0 : fadeIn ? 1 : 0,
        transitionDuration: `${FADE_DURATION}ms`,
      }}
    >
      <Lottie
        animationData={isDark ? darkAnimation : lightAnimation}
        loop
        className="w-96 h-96"
      />
      <Logo size="lg" />
    </div>
  );
}
